/**
 * Locks free sample line items to quantity 1 on the storefront (cart / product pages).
 * Intercepts Cart API (/cart/change.js, /cart/update.js, /cart/add.js) and normalises on load.
 * Only reloads the cart drawer when a sample qty was actually corrected (avoids scroll jump / image blink).
 */
(function () {
  var SAMPLE_PRODUCT_TYPE = 'product_sample_item';
  var MAX_SAMPLE_QTY = 1;
  var normalizing = false;

  function isSampleItem(item) {
    if (!item) return false;
    var type = String(item.product_type || '')
      .toLowerCase()
      .replace(/\s+/g, '_')
      .slice(0, 20);
    if (type === SAMPLE_PRODUCT_TYPE) return true;

    var props = item.properties || {};
    if (props._isSample || props._isSample === 1 || props._isSample === '1') return true;
    if (props._sample_product_id) return true;
    if (props['Sample of']) return true;

    var title = String(item.product_title || item.title || '').toLowerCase();
    if (title.indexOf('sample') !== -1 && Number(item.final_price || item.price) === 0) return true;

    return false;
  }

  function clampSampleUpdates(updates, cartItems) {
    if (!updates || !cartItems || !cartItems.length) return updates;
    var next = Object.assign({}, updates);
    cartItems.forEach(function (item) {
      if (!isSampleItem(item)) return;
      var key = String(item.variant_id);
      var lineKey = String(item.key);
      if (Object.prototype.hasOwnProperty.call(next, key)) {
        if (parseInt(next[key], 10) > MAX_SAMPLE_QTY) next[key] = MAX_SAMPLE_QTY;
      }
      if (Object.prototype.hasOwnProperty.call(next, lineKey)) {
        if (parseInt(next[lineKey], 10) > MAX_SAMPLE_QTY) next[lineKey] = MAX_SAMPLE_QTY;
      }
    });
    return next;
  }

  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) {
      return r.json();
    });
  }

  function normalizeSampleQuantities() {
    if (normalizing) return Promise.resolve(null);
    normalizing = true;

    return getCart()
      .then(function (cart) {
        var updates = {};
        var needsFix = false;
        (cart.items || []).forEach(function (item) {
          if (isSampleItem(item) && item.quantity > MAX_SAMPLE_QTY) {
            updates[item.key] = MAX_SAMPLE_QTY;
            needsFix = true;
          }
        });

        // Nothing to fix — do not reload drawer (prevents scroll jump / image blink)
        if (!needsFix) return null;

        return fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: updates }),
        }).then(function (r) {
          return r.json();
        });
      })
      .then(function (cart) {
        // Only refresh UI when we actually corrected sample quantities
        if (!cart) return null;

        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
        var drawerItems = document.querySelector('cart-drawer-items');
        if (drawerItems && typeof drawerItems.reloadCart === 'function') {
          drawerItems.reloadCart();
        }
        return cart;
      })
      .catch(function (err) {
        console.warn('[sample-quantity-lock]', err);
      })
      .finally(function () {
        normalizing = false;
      });
  }

  function patchPayloadForCartApi(url, payload, items) {
    if (!payload) return payload;

    if (/\/cart\/add\.js/.test(url) && payload.items && Array.isArray(payload.items)) {
      payload.items = payload.items.map(function (line) {
        var matched = items.find(function (i) {
          return i.variant_id === line.id || i.id === line.id;
        });
        var props = line.properties || {};
        var looksSample =
          props._isSample ||
          props._sample_product_id ||
          props['Sample of'] ||
          (matched && isSampleItem(matched));
        if (looksSample && parseInt(line.quantity, 10) > MAX_SAMPLE_QTY) {
          line.quantity = MAX_SAMPLE_QTY;
        }
        return line;
      });
    }

    if (/\/cart\/change\.js/.test(url)) {
      var lineIndex = payload.line != null ? parseInt(payload.line, 10) - 1 : -1;
      var target =
        lineIndex >= 0
          ? items[lineIndex]
          : items.find(function (i) {
              return (
                i.key === payload.id ||
                String(i.variant_id) === String(payload.id) ||
                String(i.id) === String(payload.id)
              );
            });
      if (target && isSampleItem(target) && parseInt(payload.quantity, 10) > MAX_SAMPLE_QTY) {
        payload.quantity = MAX_SAMPLE_QTY;
      }
    }

    if (/\/cart\/update\.js/.test(url) && payload.updates) {
      payload.updates = clampSampleUpdates(payload.updates, items);
    }

    return payload;
  }

  function patchFetch() {
    if (!window.fetch || window.__sampleQtyLockPatched) return;
    window.__sampleQtyLockPatched = true;
    var originalFetch = window.fetch.bind(window);

    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var method = ((init && init.method) || 'GET').toUpperCase();
      var isCartMutate =
        method === 'POST' &&
        (/\/cart\/change\.js/.test(url) || /\/cart\/update\.js/.test(url) || /\/cart\/add\.js/.test(url));

      if (!isCartMutate) {
        return originalFetch(input, init);
      }

      var bodyText = init && init.body;
      if (typeof bodyText !== 'string') {
        return originalFetch(input, init).then(function (response) {
          // Payload already clamped when possible; only normalize if samples still over limit
          response
            .clone()
            .json()
            .then(function () {
              normalizeSampleQuantities();
            })
            .catch(function () {});
          return response;
        });
      }

      var payload;
      try {
        payload = JSON.parse(bodyText);
      } catch (e) {
        return originalFetch(input, init);
      }

      return getCart()
        .then(function (cart) {
          payload = patchPayloadForCartApi(url, payload, cart.items || []);
          var nextInit = Object.assign({}, init, { body: JSON.stringify(payload) });
          return originalFetch(input, nextInit);
        })
        .then(function (response) {
          response
            .clone()
            .json()
            .then(function () {
              normalizeSampleQuantities();
            })
            .catch(function () {});
          return response;
        });
    };
  }

  window.StoriesSampleQuantityLock = {
    normalize: normalizeSampleQuantities,
    isSampleItem: isSampleItem,
  };

  patchFetch();

  // One-time check on load only (no cart:updated loop — that caused scroll reset)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', normalizeSampleQuantities);
  } else {
    normalizeSampleQuantities();
  }
})();
